import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const linkSteps = [
  { label: '技术动作', icon: 'ri-settings-3-line', color: 'primary' },
  { label: '课堂证据', icon: 'ri-file-search-line', color: 'accent' },
  { label: '专用模型', icon: 'ri-group-line', color: 'primary' },
  { label: '评价结论', icon: 'ri-award-line', color: 'accent' },
];

export default function P23Import({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.06);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.05);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* 明显可见的三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[400px] h-[400px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[420px] h-[420px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/3 right-1/3 w-[350px] h-[350px] rounded-full bg-secondary-200/20 blur-[150px] pointer-events-none animate-orb-float-2" />

      {/* 点阵背景 */}
      <div className="absolute inset-0 opacity-[0.035] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle, oklch(var(--primary-600)) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
      }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-gold border-accent-200/30 mb-6">
            <span className="gold-dot" />
            <span className="text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em]">第三部分</span>
          </div>

          <h1 className={`font-heading text-3xl md:text-5xl lg:text-[3.5rem] font-bold mb-4 leading-tight transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">AI如何评一堂思政课</span>
          </h1>
          <p className={`font-heading text-lg md:text-xl text-foreground-950 mb-6 transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            原理、证据与案例验证
          </p>

          {/* 四段式链路 */}
          <div className={`flex flex-wrap items-center justify-center gap-3 md:gap-6 mb-8 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            {linkSteps.map((step, i) => (
              <div key={step.label} className="flex items-center gap-0">
                <div className={`flex items-center gap-3 px-5 py-3 rounded-xl ${step.color === 'primary' ? 'glass-blue border-primary-200/40' : 'glass-gold border-accent-200/30'} transition-all duration-500 hover:scale-105`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${step.color === 'primary' ? 'bg-primary-100/50' : 'bg-accent-100/50'}`}>
                    <i className={`${step.icon} text-base ${step.color === 'primary' ? 'text-primary-600' : 'text-accent-600'}`} />
                  </div>
                  <span className="text-base font-body font-semibold text-foreground-950 whitespace-nowrap">{step.label}</span>
                </div>
                {i < linkSteps.length - 1 && (
                  <div className="flex-shrink-0 w-8 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-xl text-foreground-950" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 底部标注 */}
          <p className={`text-base text-foreground-950 font-body font-semibold transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
            技术原理、课堂证据与实测数据同步呈现
          </p>
        </div>

        {/* 核心说明卡片 */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="p-8 rounded-2xl glass-liquid border-primary-200/40 hover:border-primary-300/60 transition-all duration-300 hover:-translate-y-1">
            <div className="relative z-10">
              <h3 className="font-heading text-3xl font-bold text-foreground-950 mb-4">系统看到了什么</h3>
              <p className="text-lg text-foreground-950 font-body leading-relaxed">课堂视频、音频、课件和教材进入系统后，被整理成课堂实录，完成有效切片与多模态识别</p>
            </div>
          </div>
          <div className="p-8 rounded-2xl glass-liquid-gold border-accent-200/40 hover:border-accent-300/60 transition-all duration-300 hover:-translate-y-1">
            <div className="relative z-10">
              <h3 className="font-heading text-3xl font-bold text-foreground-950 mb-4">依据什么作出判断</h3>
              <p className="text-lg text-foreground-950 font-body leading-relaxed">关键片段映射到评价指标，形成证据链、维度判断和质量分层</p>
            </div>
          </div>
          <div className="p-8 rounded-2xl glass-liquid border-primary-200/40 hover:border-primary-300/60 transition-all duration-300 hover:-translate-y-1">
            <div className="relative z-10">
              <h3 className="font-heading text-3xl font-bold text-foreground-950 mb-4">能否被验证及泛化</h3>
              <p className="text-lg text-foreground-950 font-body leading-relaxed">大赛9专家金标验证一致性0.83，泛化后分档单调率89%</p>
            </div>
          </div>
        </div>

        {/* 连接下一页提示 */}
        <div className={`mt-12 text-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-3 px-7 py-4 rounded-xl glass border-white/50 hover:border-primary-200/50 transition-all duration-300">
            <i className="ri-arrow-right-line text-lg text-primary-500" />
            <span className="text-lg font-body text-foreground-950">
              先用六步流程把课堂输入、技术处理、证据生成和评价输出放到同一条链上
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}